// The effect registry: one catalog wire id in, one recipe out (R910, R924,
// `effects.md` §1, `ui-map-interface.md` §6.6).
//
// An effect is a slow visual animation a player wears on a fleet, a round or
// a hold. The server resolves who wears what and stamps one catalog wire id
// on the object; this module turns that number into the draw code.
//
// **The arithmetic is not here.** `effect-recipe-kit.ts` holds the row and
// context types, the clocks, the hashes, the alpha clamps, the atlas cell
// keys and the eight families. This module re-exports the kit, so a reader
// and an importer take one name from one place.
//
// **A code resolves through the three recipe maps first.** The owner picked
// fifty-four cards over mockup rounds 041, 042 and 043, and the draw code of
// a picked card lives in `effect-recipes-fleet.ts`,
// `effect-recipes-torpedo.ts` or `effect-recipes-hold.ts`, keyed by the
// catalog code. A code no map carries falls back to the FAMILY its look row
// names, so every row of the catalog draws something on the day its data
// lands and the recipes arrive one category at a time.
//
// This is a leaf (`leaves.test.ts`).

import { EFFECTS } from '../gen/presentation';
import {
  EFFECT_STYLES, isEffectFamily,
  type EffectCtx, type EffectFamily, type EffectRecipeFn, type EffectRow,
} from './effect-recipe-kit';
import { RECIPES as FLEET_RECIPES } from './effect-recipes-fleet';
import { RECIPES as TORPEDO_RECIPES } from './effect-recipes-torpedo';
import { RECIPES as HOLD_RECIPES } from './effect-recipes-hold';

export * from './effect-recipe-kit';

/**
 * The three recipe maps, in the order the registry reads them.
 *
 * A catalog code is unique across the three categories, so the order settles
 * nothing but the cost of the lookup. It is read ONE time, when
 * `EFFECT_BY_WIRE` is built.
 */
const RECIPE_MAPS: readonly Readonly<Record<string, EffectRecipeFn>>[] = [
  FLEET_RECIPES, TORPEDO_RECIPES, HOLD_RECIPES,
];

/**
 * A catalog row joined to the code that draws it.
 *
 * `own` states where the draw code came from: `recipe` is the card's own
 * function out of one of the three maps, `family` the fallback the row's
 * look names. The eye test and the layer read `draw` alone; `own` is for a
 * test and for a reader who asks which cards are built.
 */
export interface EffectRecipe {
  readonly row: EffectRow;
  readonly family: EffectFamily;
  readonly own: 'recipe' | 'family';
  draw(c: EffectCtx): void;
}

/** The card's own recipe for a code, or undefined where no map carries it. */
export function recipeFnOf(code: string): EffectRecipeFn | undefined {
  for (const map of RECIPE_MAPS) {
    const fn = map[code];
    if (fn) return fn;
  }
  return undefined;
}

/**
 * A row joined to what draws it, or undefined where the family is unknown.
 *
 * A row keeps its `family` whichever way it resolves, because the family is
 * what the look row states and what a reader of the catalog reads.
 */
export function recipeOf(row: EffectRow): EffectRecipe | undefined {
  if (!isEffectFamily(row.family)) return undefined;
  const own = recipeFnOf(row.code);
  const draw = own ?? EFFECT_STYLES[row.family];
  return {
    row,
    family: row.family,
    own: own ? 'recipe' : 'family',
    draw: (c) => draw(row, c),
  };
}

/**
 * Every catalog row by its frozen wire id, built one time.
 *
 * The wire carries the NUMBER and the client indexes it directly, so a
 * frame does no lookup by code. A hole in the array is a wire id this build
 * does not know, and the layer draws nothing for it. A retired wire id is a
 * hole for ever (`effects.md` §1).
 */
export const EFFECT_BY_WIRE: readonly (EffectRecipe | undefined)[] = (() => {
  const rows = EFFECTS as readonly EffectRow[];
  let top = 0;
  for (const r of rows) if (r.wire > top) top = r.wire;
  const out: (EffectRecipe | undefined)[] = new Array(top + 1).fill(undefined);
  for (const r of rows) out[r.wire] = recipeOf(r);
  return out;
})();
